import React from 'react';
import './RunnerCv.css';

const PROFILE_SRC = '/images/profile.jpg';

const shootJobs = [
  {
    company: 'Truth Creative',
    companyHref: 'https://www.truth-creative.co.uk/',
    location: null,
    badge: 'Contract',
    dates: 'Jun 2025 – Feb 2026',
    title: 'Motion Designer & Videographer',
    groups: [
      {
        label: 'On set',
        bullets: [
          'Led small-crew branded shoots at client sites across the UK',
          'Set up lighting, camera and audio for client interviews, green screen capture and multicam shoots',
          'Conducted client interviews on camera, refining the script on shoot day',
        ],
      },
      {
        label: 'In post',
        bullets: [
          'Edited raw footage to final delivery: multicam sync, cut to script and colour grade',
          'Keyed and composited the green screen footage and built motion graphics into the final edits',
          'Ran client review rounds and turned around amends to deadline',
        ],
      },
    ],
  },
  {
    company: 'Box Bear',
    companyHref: 'https://boxbear.co.uk/',
    location: null,
    badge: 'In-house studio',
    dates: 'May 2022 – May 2024',
    title: 'Motion Graphic Designer & Production Assistant',
    groups: [
      {
        label: 'In the studio',
        bullets: [
          'Planned and filmed interviews with healthcare professionals for pharmaceutical clients',
          'Set up the in-house studio for shoots and live sessions, including cameras, lighting and audio',
          'Ran the multicamera and live-stream kit for virtual reality meetings, with healthcare professionals joining live from around the world',
        ],
      },
      {
        label: 'In post',
        bullets: [
          "Edited the footage into informative videos shaped to each client's brief, from talking-head interviews to pieces led by 3D graphics",
          'Designed motion graphics in After Effects and 3D assets in Cinema 4D, assembled in Premiere Pro',
          'Responsible for organisation of media and versioning on each project through to final export',
        ],
      },
    ],
  },
  {
    company: 'Freelance',
    companyHref: 'https://uk.linkedin.com/in/seanderham',
    location: null,
    badge: 'Alongside employed roles',
    dates: 'Jun 2021 – May 2025',
    title: 'Motion Designer & Editor',
    body: 'Edited multicamera interviews and motion content for clients across sectors.',
  },
];

const hospitalityJob = {
  company: 'The Championships, Wimbledon',
  companyHref: 'https://www.wimbledon.com/',
  badge: 'Seasonal',
  dates: '2018, 2019, 2021',
  title: 'Media Bar & Restaurant Team Lead',
  bullets: [
    'Ran the media bar and restaurant for press and players across the Championships fortnight',
    'Built shift rotas, trained new starters, placed orders and kept stock under control',
    'Handled guest requests directly and kept service moving through the busiest sessions',
  ],
};

const otherJob = {
  company: 'The Adare Collection',
  companyHref: 'https://theadarecollection.com/',
  badge: 'Remote',
  dates: 'Mar 2025 – present',
  title: 'Lead Web Designer',
  bullets: [
    'Designed and coded the company website from a blank file, including interactive maps and virtual tours',
    'Look after the live site day to day, working with the team to get new features out on deadline',
  ],
};

const skills = [
  'Camera and lighting setup, including multicam and green screen',
  'Adobe Suite (Premiere Pro, After Effects and Photoshop), DaVinci Resolve: multicam syncing, rushes management',
  'Blender, Cinema 4D and Unreal Engine, so I know how CG fits into a VFX pipeline',
  'Guest and client care, stock control, rotas and ordering',
];

const JobEntry = ({ job }) => (
  <div className="runner-cv__job">
    <div className="runner-cv__job-top">
      <h3>
        {job.companyHref ? (
          <a href={job.companyHref} target="_blank" rel="noopener noreferrer">
            {job.company}
          </a>
        ) : (
          job.company
        )}
        {job.location ? (
          <span className="runner-cv__badge runner-cv__badge--quiet">
            {job.location}
          </span>
        ) : null}
        {job.badge ? <span className="runner-cv__badge">{job.badge}</span> : null}
      </h3>
      <span className="runner-cv__dates">{job.dates}</span>
    </div>
    <h4>{job.title}</h4>
    {job.groups ? (
      <div className="runner-cv__groups">
        {job.groups.map((group) => (
          <div className="runner-cv__group" key={group.label}>
            <p className="runner-cv__group-label">{group.label}</p>
            <ul className="runner-cv__bullets">
              {group.bullets.map((bullet) => (
                <li key={bullet}>{bullet}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    ) : job.bullets ? (
      <ul className="runner-cv__bullets">
        {job.bullets.map((bullet) => (
          <li key={bullet}>{bullet}</li>
        ))}
      </ul>
    ) : (
      <p>{job.body}</p>
    )}
  </div>
);

// Unlinked page: reachable only by typing the URL directly. Kept free of the
// site header and footer so it reads as a standalone CV.
const RunnerCv = () => {
  return (
    <div className="runner-cv-page">
      <article className="runner-cv">
        <header className="runner-cv__header">
          <div className="runner-cv__intro">
            <h1 className="runner-cv__name">Sean Derham</h1>
            <p className="runner-cv__role">Production Runner</p>
            <p className="runner-cv__location">
              London · Willing to travel · Available immediately
            </p>
            <p className="runner-cv__licence">Full UK driving licence</p>
            <div className="runner-cv__contacts runner-cv__contacts--screen">
              <a href="tel:+447597368482">07597 368482</a>
              <a href="mailto:sjderham@protonmail.com">sjderham@protonmail.com</a>
              <a
                href="https://seanderham.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                seanderham.com
              </a>
              <a
                href="https://uk.linkedin.com/in/seanderham"
                target="_blank"
                rel="noopener noreferrer"
              >
                LinkedIn
              </a>
            </div>
            <div className="runner-cv__contacts runner-cv__contacts--print">
              <span>
                07597 368482 · sjderham@protonmail.com · seanderham.com ·
                linkedin.com/in/seanderham
              </span>
            </div>
          </div>
          <img
            className="runner-cv__photo"
            src={PROFILE_SRC}
            alt="Sean Derham"
            width={112}
            height={112}
          />
        </header>

        <section className="runner-cv__section">
          <h2>Profile</h2>
          <p>
            Four years of making video content for brands, on shoot days and in
            the edit, have given me a solid grounding in how a production comes
            together. I&apos;m confident both in studio and on location, with
            experience setting up camera, lighting and audio for interviews and
            multicam shoots. The edit is where I&apos;ve spent most of my time
            and done my best work, and it&apos;s where I want to build my
            career. Between client shoots and the films I make on my own Sony
            FX3, I&apos;ve often found myself wondering how the bigger
            productions pull it off. Now I&apos;m moving into the industry as a
            runner to find out for myself.
          </p>
        </section>

        <section className="runner-cv__section" id="cv-work">
          <h2>Shoot &amp; Production Experience</h2>
          <div className="runner-cv__jobs">
            {shootJobs.map((job) => (
              <JobEntry key={`${job.company}-${job.dates}`} job={job} />
            ))}
          </div>
        </section>

        <section className="runner-cv__section">
          <h2>Hospitality</h2>
          <div className="runner-cv__jobs">
            <JobEntry job={hospitalityJob} />
          </div>
        </section>

        <section className="runner-cv__section">
          <h2>Other Employment</h2>
          <div className="runner-cv__jobs">
            <JobEntry job={otherJob} />
          </div>
        </section>

        <section className="runner-cv__section">
          <h2>Personal Projects</h2>
          <p>
            In my spare time I shoot and edit short films on my own Sony FX3.
          </p>
        </section>

        <section className="runner-cv__section">
          <h2>Skills &amp; Kit</h2>
          <ul className="runner-cv__skill-list">
            {skills.map((skill) => (
              <li key={skill}>{skill}</li>
            ))}
          </ul>
        </section>

        <section className="runner-cv__section">
          <h2>Education</h2>
          <div className="runner-cv__job">
            <div className="runner-cv__job-top">
              <h3>University of Sussex</h3>
              <span className="runner-cv__dates">2018 – 2021</span>
            </div>
            <p>First Class Honours, Product Design</p>
          </div>
        </section>

        <section className="runner-cv__section">
          <h2>Training</h2>
          <ul className="runner-cv__skill-list">
            <li>ScreenSkills e-learning modules completed</li>
          </ul>
        </section>

        <p className="runner-cv__disclaimer">
          This CV may be kept on file and distributed for employment purposes.
        </p>
      </article>
    </div>
  );
};

export default RunnerCv;
